import { useEffect, useState } from 'react';
import { getListPrice } from 'src/common/hooks/widgets/fetchData';
import { useCurrencyWithSymbol } from 'src/common/hooks/widgets/useCurrencyWithSymbol';
import {
  ADVANCED_MIN,
  ADVANCED_PN,
  BASE_MIN,
  BASE_PN,
} from '../constants/prices';
import { ADVANCED, BASE } from '../constants/spread';

/**
 * Calculate the estimated price
 *
 * @param {object} intl used for the formatting
 * @param {string} lang the language code to get the currency sign for
 * @param {number} employees : Number of employees/workstations
 * @param {string} offering : The offerings selected
 * @param {number} units : units selected
 * @returns {Array}
 */

export interface PropsFunc {
  currency: string;
  localeCode: string;
  country: string;
  offering: string;
  units: number;
}

interface QueryData {
  ITEM_TERM: number;
  ITEM_NUM: number;
  PART_NUM: string;
  QTY: number;
  BILL_FREQ: string;
}

const partsToQuery: QueryData[] = [
  {
    ITEM_TERM: 1,
    ITEM_NUM: 0,
    PART_NUM: BASE_PN,
    QTY: 0,
    BILL_FREQ: 'U',
  },
  {
    ITEM_TERM: 1,
    ITEM_NUM: 1,
    PART_NUM: ADVANCED_PN,
    QTY: 0,
    BILL_FREQ: 'U',
  },
];

export interface PriceProps {
  ITEM_NUM: string;
  UNIT_PRIC: string;
  EXT_PRICE: string;
  PART_NUM: string;
}

type StreetPriceRequest = {
  HEADER_IN: {
    COUNTRY: string;
    CURRENCY: string;
    FOR_STREET_PRICING: boolean;
  };
  ITEMS_IN: typeof partsToQuery;
};

export const usePrice = ({
  currency,
  localeCode,
  country,
  offering,
  units,
}: PropsFunc) => {
  const [priceFile, setPriceFile] = useState({} as PriceProps[]);
  const [fetchingError, setFetchingError] = useState({});
  const [price, setPrice] = useCurrencyWithSymbol(currency, localeCode, 0);
  const [basePrice, setBasePrice] = useState(0);
  const [advancedPrice, setAdvancedPrice] = useState(0);

  const calculatePrice = (offering: string) => {
    switch (offering) {
      case BASE:
        const basePrice = priceFile?.find(
          (items: PriceProps) => items.PART_NUM == BASE_PN,
        )?.EXT_PRICE;
        return basePrice ? Math.round(Number(basePrice)) : 0;
      case ADVANCED:
        const advancedPrice = priceFile?.find(
          (items: PriceProps) => items.PART_NUM == ADVANCED_PN,
        )?.EXT_PRICE;
        return advancedPrice ? Math.round(Number(advancedPrice)) : 0;
      default:
        return 0;
    }
  };

  const fetchPrices = () => {
    setBasePrice(BASE_MIN);
    setAdvancedPrice(ADVANCED_MIN);
    partsToQuery.map((items: QueryData) => (items.QTY = units));
    const body: StreetPriceRequest = {
      HEADER_IN: {
        COUNTRY: country,
        CURRENCY: currency,
        FOR_STREET_PRICING: true,
      },
      ITEMS_IN: partsToQuery,
    };
    getListPrice(
      `https://www.ibm.com/marketplace/purchase/catalog/resources/price/calculation`,
      JSON.stringify(body),
      'POST',
    )
      .then((data) => {
        if (data.HEADER_OUT.ERROR_FLAG === '0') {
          setPriceFile(data.ITEMS_OUT);
        } else {
          setPriceFile([]);
        }
      })
      .catch((error) => {
        console.log('Error:', error);
        setPriceFile([]);
        if (error.response) {
          setFetchingError(error.response);
        }
      });
  };

  useEffect(() => {
    fetchPrices();
  }, [localeCode, currency, country, units, offering]);

  useEffect(() => {
    if (priceFile?.length) {
      const price = units > 0 ? calculatePrice(offering) : 0;
      setBasePrice(price);
      setAdvancedPrice(price);
      setPrice(price);
    }
  }, [priceFile]);

  return [price, basePrice, advancedPrice, fetchingError];
};
